import { useAudioPlayer, useAudioPlayerStatus } from 'expo-audio';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  ReactNode,
} from 'react';
import { useAssetDownloader } from '@/contexts/DownloadAssetContext';
import {
  emptyAudioCache,
  reducer,
  setAudioCache,
  setAudioModeSetup,
  trackedDataSetup,
} from './helpers';
import { INITIAL_STATE } from './constants';
import { AudioActions, AudioPayload, AudioState } from './types';
import { logError } from '@/utils/logHandling';
import { IS_WEB } from '@/utils/platform';

// Split in two to optimize re-renders: consumers of the actions don't need to re-render
// when the state changes, and consumers of the state don't need
// to re-render when the actions change. The actions are stable across ticks,
// so they can be memoized and passed down without causing re-renders.
export const AudioActionsContext = createContext<AudioActions | undefined>(
  undefined,
);
export const AudioStateContext = createContext<AudioState | undefined>(
  undefined,
);

export const AudioContextProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [context, dispatch] = useReducer(reducer, INITIAL_STATE);
  const player = useAudioPlayer(undefined, { keepAudioSessionActive: true });
  const status = useAudioPlayerStatus(player);
  const trackedKey = useRef<string | null>(null);
  const trackedData = useRef<number[] | null>(null);
  const wasPlayerPlayingBeforePause = useRef<boolean>(false);

  const contextRef = useRef<AudioState>(context);
  contextRef.current = context;

  // On teardown, React runs the parent's cleanup before its children's, so
  // this provider's cleanup frees the shared player first — while children
  // (e.g. AudioPlayer) can still call stopSound/unloadSound from their own
  // cleanups. We set this false there so those late calls no-op instead of
  // touching the freed player and triggering errors.
  const isActiveRef = useRef<boolean>(true);
  const { getLocalUri } = useAssetDownloader();
  const getLocalUriRef = useRef(getLocalUri);
  getLocalUriRef.current = getLocalUri;

  const alterContext = useCallback((payload: AudioPayload) => {
    dispatch({
      type: 'KEY_CHANGE',
      payload,
    });
  }, []);

  const resetContext = useCallback(() => {
    alterContext({
      source: '',
      position: 0,
      duration: 0,
      isPlaying: false,
      isLoading: true,
      playbackRate: 1,
    });
  }, [alterContext]);

  const getAudioStatus = useCallback(() => {
    if (!isActiveRef.current) {
      return;
    }
    try {
      return {
        isLoaded: player.isLoaded,
        isPlaying: player.playing,
        positionMillis: Math.round(player.currentTime * 1000),
        durationMillis: Math.round(player.duration * 1000),
      };
    } catch (error) {
      logError('Could not get audio status', error);
    }
  }, [player]);

  // expo-audio's replace() loads the source asynchronously and returns no
  // promise (unlike expo-av's awaitable loadAsync). Calling play() before the
  // source is ready is a no-op on native, and duration reads as 0/NaN, so we
  // wait for the player to report a loaded source with a real duration.
  const waitUntilPlayable = useCallback(
    (timeoutMs = 5000): Promise<void> =>
      new Promise((resolve) => {
        const isPlayable = () =>
          player.isLoaded &&
          Number.isFinite(player.duration) &&
          player.duration > 0;
        if (isPlayable()) {
          return resolve();
        }
        let timer: ReturnType<typeof setTimeout>;
        const subscription = player.addListener(
          'playbackStatusUpdate',
          (status) => {
            if (
              status.isLoaded &&
              Number.isFinite(status.duration) &&
              status.duration > 0
            ) {
              clearTimeout(timer);
              subscription.remove();
              resolve();
            }
          },
        );
        timer = setTimeout(() => {
          subscription.remove();
          resolve();
        }, timeoutMs);
      }),
    [player],
  );

  const lastPublished = useRef({
    position: -1,
    duration: -1,
    isPlaying: false,
  });

  const publishStatus = useCallback(
    (position: number, duration: number, isPlaying: boolean) => {
      const last = lastPublished.current;
      if (
        last.position === position &&
        last.duration === duration &&
        last.isPlaying === isPlaying
      ) {
        return;
      }
      lastPublished.current = { position, duration, isPlaying };
      setAudioCache(trackedKey.current, `${position}-${duration}`);
      dispatch({
        type: 'KEY_CHANGE',
        payload: { isPlaying, position, duration },
      });
    },
    [],
  );

  // Mirror the player's reactive status into the millisecond-based context the
  // UI consumes. useAudioPlayerStatus re-renders on every playbackStatusUpdate,
  // so this stays in sync without a manual addListener subscription.
  useEffect(() => {
    if (!status?.isLoaded) {
      return;
    }
    const position = Math.round(status.currentTime * 1000);
    const duration = Math.round(status.duration * 1000);
    publishStatus(position || 0, duration || 0, status.playing);
  }, [status, publishStatus]);

  // Web-only fallback: expo-audio emits status on the media element's
  // 'timeupdate' but honours no updateInterval, so the reactive status alone
  // can leave the scrubber frozen mid-playback. Native advances via the
  // updateInterval, so the poll is scoped to web. It stops itself at genuine
  // end-of-track; flipping isPlaying on a transient stall would freeze it.
  useEffect(() => {
    if (!IS_WEB || !context.isPlaying) {
      return;
    }
    const id = setInterval(() => {
      if (!player.isLoaded) {
        return;
      }
      const position = Math.round(player.currentTime * 1000);
      const duration = Math.round(player.duration * 1000);
      if (!Number.isFinite(position) || !Number.isFinite(duration)) {
        return;
      }
      const hasEnded = duration > 0 && position >= duration;
      publishStatus(position, duration, !hasEnded);
    }, 250);
    return () => clearInterval(id);
  }, [context.isPlaying, player, publishStatus]);

  const playSound = useCallback(
    async (trackedData?: number[] | null) => {
      try {
        if (trackedData) {
          const didFinish = trackedData[0] / trackedData[1] >= 0.99;
          if (didFinish) {
            await player.seekTo(0);
          } else {
            await player.seekTo(trackedData[0] / 1000);
          }
        } else {
          const { position, duration } = contextRef.current;
          const didFinish = position / duration >= 0.99;
          if (didFinish) {
            await player.seekTo(0);
          }
        }
        player.play();

        alterContext({ isPlaying: true });
      } catch (error) {
        logError('Could not play sound', error);
      }
    },
    [player, alterContext],
  );

  const pauseSound = useCallback(async () => {
    try {
      const status = getAudioStatus();
      if (!status?.isLoaded) {
        return;
      }
      wasPlayerPlayingBeforePause.current = status?.isPlaying;
      player.pause();
      alterContext({ isPlaying: false });
    } catch (error) {
      logError('Error while pausing running audio', error);
    }
  }, [getAudioStatus, player, alterContext]);

  const stopSound = useCallback(async () => {
    try {
      const status = getAudioStatus();
      if (!status?.isLoaded) {
        return;
      }
      // expo-audio has no stop; pausing and rewinding matches the old
      // stopAsync semantics (halt playback and reset to the start).
      player.pause();
      await player.seekTo(0);
      alterContext({ isPlaying: false, position: 0 });
    } catch (error) {
      logError('Error while stopping running audio', error);
    }
  }, [getAudioStatus, player, alterContext]);

  const unloadSound = useCallback(async () => {
    try {
      const status = getAudioStatus();
      if (!status?.isLoaded) {
        return;
      }
      // The single player instance is reused across sources via replace(),
      // so "unloading" only needs to halt and rewind the current source.
      player.pause();
      await player.seekTo(0);
      alterContext({ isPlaying: false });
    } catch (error) {
      logError('Error while unloading active audio', error);
    }
  }, [getAudioStatus, player, alterContext]);

  const loadAndPlaySound = useCallback(
    async (uri: string, uniqueKey: string, playbackRate = 1) => {
      try {
        const prevSource =
          contextRef.current.source + (trackedKey.current ?? '');
        const currentSource = uri + (uniqueKey ?? '');
        const isSameAudio = prevSource === currentSource;
        const status = getAudioStatus();
        // This is true when the player was played & paused without changing the instance
        if (isSameAudio && status?.isLoaded) {
          return await playSound();
        }
        await unloadSound();
        trackedKey.current = uniqueKey ?? null;
        const offlineURI = await getLocalUriRef.current(uri);

        const initialPositionMillis = trackedData.current
          ? trackedData.current[0]
          : 0;

        alterContext({ source: uri, isLoading: true });
        player.replace({ uri: offlineURI });

        // replace() is fire-and-forget; wait for the source to finish loading
        // before reading its duration or attempting to play it.
        if (!IS_WEB) {
          await waitUntilPlayable();
        }

        player.setPlaybackRate(playbackRate, 'high');

        const loadedStatus = getAudioStatus();
        alterContext({
          source: uri,
          position: initialPositionMillis,
          duration: loadedStatus?.durationMillis ?? 0,
          isLoading: false,
        });
        // playSound applies the tracked start position via seekTo.
        await playSound(trackedData.current);
      } catch (error) {
        logError('Could not load or play sound', error);
      }
    },
    [
      getAudioStatus,
      playSound,
      unloadSound,
      alterContext,
      player,
      waitUntilPlayable,
    ],
  );

  const init = useCallback(
    async (
      uri: string,
      autoPlay: boolean,
      isTracked: boolean,
      uniqueKey: string,
      playbackRate?: number,
    ) => {
      try {
        await setAudioModeSetup();
        // Unloading any previously loaded sounds (to maintain only one instance)
        if (isTracked && uniqueKey) {
          const cachedData = await trackedDataSetup(uniqueKey);
          if (cachedData) {
            const cachedDataNumbers = cachedData.map((eachCache) =>
              Number(eachCache),
            );
            trackedData.current = cachedDataNumbers;
            alterContext({
              source: uri,
              position: cachedDataNumbers[0],
              duration: cachedDataNumbers[1],
              isPlaying: false,
              isLoading: false,
            });
          } else {
            trackedData.current = null;
          }
        } else {
          trackedData.current = null;
        }
        if (!autoPlay) {
          return alterContext({
            isLoading: false,
          });
        }
        await unloadSound();
        await loadAndPlaySound(uri, uniqueKey, playbackRate);
      } catch (error) {
        logError('Error while initializing audio instance:', error);
        throw error;
      }
    },
    [alterContext, unloadSound, loadAndPlaySound],
  );

  const changeAudioPosition = useCallback(
    async (value: number) => {
      try {
        const status = getAudioStatus();
        if (!status?.isLoaded || !status.durationMillis) {
          return;
        }
        const newPosition = value * status.durationMillis;
        alterContext({ position: newPosition });
        const cachedValue = `${newPosition}-${status.durationMillis}`;
        await Promise.all([
          player.seekTo(newPosition / 1000),
          setAudioCache(trackedKey.current, cachedValue),
        ]);
        if (!wasPlayerPlayingBeforePause.current) {
          return;
        }
        await playSound();
      } catch (error) {
        logError('ERROR while changing audio position', error);
      }
    },
    [getAudioStatus, alterContext, player, playSound],
  );

  const moveBack = useCallback(async () => {
    try {
      const status = getAudioStatus();
      if (!status?.isLoaded) {
        return;
      }
      await pauseSound();
      const currentPosition = status.positionMillis;
      const secondsInMil = 15000;
      const expectedPosition = currentPosition - secondsInMil;
      const derivedPosition = Math.max(expectedPosition, 0);
      await player.seekTo(derivedPosition / 1000);
      player.play();
      alterContext({ isPlaying: true });
    } catch (error) {
      logError('Could not pause sound', error);
    }
  }, [getAudioStatus, pauseSound, player, alterContext]);

  useEffect(() => {
    return () => {
      (async () => {
        try {
          if (IS_WEB) {
            await unloadSound();
          }
        } catch (error) {
          logError('Could not unload sound', error);
        } finally {
          isActiveRef.current = false;
          emptyAudioCache();
        }
      })();
    };
  }, []);

  const actions: AudioActions = useMemo(
    () => ({
      init,
      loadAndPlaySound,
      pauseSound,
      stopSound,
      unloadSound,
      changeAudioPosition,
      moveBack,
      resetContext,
    }),
    [
      init,
      loadAndPlaySound,
      pauseSound,
      stopSound,
      unloadSound,
      changeAudioPosition,
      moveBack,
      resetContext,
    ],
  );

  return (
    <AudioActionsContext.Provider value={actions}>
      <AudioStateContext.Provider value={context}>
        {children}
      </AudioStateContext.Provider>
    </AudioActionsContext.Provider>
  );
};
